import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsEnvelope, BsPerson } from 'react-icons/bs';
import { GoDash } from 'react-icons/go';
import { IoFemaleOutline, IoMaleOutline } from 'react-icons/io5';
import { TbMessage } from 'react-icons/tb';
import { useLoaderData } from 'react-router';
import { z } from 'zod';

import Button from '@/components/Button';
import Dropdown from '@/components/Dropdown';
import PhoneNumberInput, {
  type PhoneNumberInputValue,
} from '@/components/PhoneNumberInput';
import ResponsiveModalBottomSheet from '@/components/ResponsiveModalBottomSheet';
import TextInput from '@/components/TextInput';
import { Gender, type Therapist } from '@/models';
import { useBookingStore } from '@/store/bookingStore';
import { phoneSuperRefine } from '@/utils/validation';

export interface CustomerDetails {
  name?: string;
  email?: string;
  tel?: string;
  telPrefix: string;
  remark?: string;
  useContactInfo?: boolean;
  therapistPreference?: {
    gender?: Gender;
    preferredTherapistIds?: number[];
    unpreferredTherapistIds?: number[];
  };
}

interface CustomerDetailsFormModalBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (details: CustomerDetails) => void;
  initialData?: Partial<CustomerDetails>;
  customerIndex: number;
}

interface CustomerForm {
  name?: string;
  email?: string;
  tel?: string;
  telPrefix?: string;
  remark?: string;
}

const CustomerDetailsFormModalBottomSheet: React.FC<
  CustomerDetailsFormModalBottomSheetProps
> = ({ isOpen, onClose, onSave, initialData = {}, customerIndex }) => {
  const { t } = useTranslation();
  const loaderData = useLoaderData<{ therapists: Therapist[] }>();
  const { therapists } = loaderData;
  const {
    name,
    email,
    tel,
    telPrefix: contactTelPrefix,
    customers,
  } = useBookingStore();

  const isContactInfoUsedByOthers = customers.some(
    (customer, index) => index !== customerIndex && customer.useContactInfo,
  );

  const [useContactInfo, setUseContactInfo] = useState(
    initialData.useContactInfo || false,
  );
  const [genderPreference, setGenderPreference] = useState<Gender | undefined>(
    initialData.therapistPreference?.gender,
  );
  const [preferredTherapists, setPreferredTherapists] = useState<number[]>(
    initialData.therapistPreference?.preferredTherapistIds || [],
  );
  const [unpreferredTherapists, setUnpreferredTherapists] = useState<number[]>(
    initialData.therapistPreference?.unpreferredTherapistIds || [],
  );

  // Create schema with validation message keys (translation happens during rendering)
  const validationSchema = z
    .object({
      name: z.string().optional(),
      email: z
        .string()
        .optional()
        .refine((val) => !val || z.string().email().safeParse(val).success, {
          message: 'validation.invalidEmail',
        }),
      tel: z.string().optional(),
      telPrefix: z.string().optional(),
      remark: z.string().optional(),
    })
    .superRefine(phoneSuperRefine('tel', 'telPrefix', '+66', true));

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CustomerForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      tel: '',
      telPrefix: contactTelPrefix || '+66',
      remark: '',
    },
  });

  // Initialize form values when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      const shouldUseContactInfo = initialData.useContactInfo || false;

      // Update checkbox state
      setUseContactInfo(shouldUseContactInfo);

      // Update therapist preference states
      setGenderPreference(initialData.therapistPreference?.gender);
      setPreferredTherapists(
        initialData.therapistPreference?.preferredTherapistIds || [],
      );
      setUnpreferredTherapists(
        initialData.therapistPreference?.unpreferredTherapistIds || [],
      );

      if (shouldUseContactInfo) {
        // Use root-level contact info
        reset({
          name: name || '',
          email: email || '',
          tel: tel || '',
          telPrefix: contactTelPrefix || '+66',
          remark: initialData.remark || '',
        });
      } else {
        // Use customer's own data
        reset({
          name: initialData.name || '',
          email: initialData.email || '',
          tel: initialData.tel || '',
          telPrefix: initialData.telPrefix || '+66',
          remark: initialData.remark || '',
        });
      }
    }
  }, [isOpen, initialData, name, email, tel, contactTelPrefix, reset]);

  const handleUseContactInfoToggle = (checked: boolean) => {
    // Don't allow toggling if another customer is using contact info
    if (isContactInfoUsedByOthers && checked) {
      return;
    }

    setUseContactInfo(checked);
    if (checked) {
      // Use root-level contact info
      setValue('name', name || '');
      setValue('email', email || '');
      setValue('tel', tel || '');
      setValue('telPrefix', contactTelPrefix || '+66', {
        shouldValidate: true,
      });
    } else {
      // Use customer's own data
      setValue('name', initialData.name || '');
      setValue('email', initialData.email || '');
      setValue('tel', initialData.tel || '');
      setValue('telPrefix', initialData.telPrefix || '+66', {
        shouldValidate: true,
      });
    }
  };

  const getGenderLabel = (gender: Gender) => {
    switch (gender) {
      case Gender.Any:
        return t('gender.any');
      case Gender.Male:
        return t('gender.male');
      case Gender.Female:
        return t('gender.female');
      case Gender.LGBTQIA:
        return t('gender.lgbtqia');
      default:
        return t('gender.any');
    }
  };

  const genderOptions = [
    {
      value: Gender.Any,
      label: t('gender.any'),
      icon: <GoDash className="text-secondary-text size-6" />,
    },
    {
      value: Gender.Male,
      label: t('gender.male'),
      icon: <IoMaleOutline className="text-male size-6" />,
    },
    {
      value: Gender.Female,
      label: t('gender.female'),
      icon: <IoFemaleOutline className="text-female size-6" />,
    },
    {
      value: Gender.LGBTQIA,
      label: t('gender.lgbtqia'),
      icon: <img src="/icons/lgbtq.png" alt="LGBTQIA+" className="size-6" />,
    },
  ];

  const therapistOptions = therapists.map((therapist) => ({
    value: therapist.therapistId,
    label: therapist.name,
    suffix: getGenderLabel(therapist.gender),
  }));

  const submitDetails = (data: CustomerForm) => {
    onSave({
      name: data.name,
      email: data.email,
      telPrefix: data.telPrefix || '+66',
      tel: data.tel,
      remark: data.remark,
      useContactInfo,
      therapistPreference: {
        gender: genderPreference,
        preferredTherapistIds: preferredTherapists,
        unpreferredTherapistIds: unpreferredTherapists,
      },
    });
  };

  return (
    <ResponsiveModalBottomSheet
      title={t('customerDetails.title')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <form
        onSubmit={handleSubmit(submitDetails)}
        className="flex h-full flex-col"
      >
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex items-center gap-2">
            <label
              className={`flex items-center gap-2 ${isContactInfoUsedByOthers ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                checked={useContactInfo}
                onChange={(e) => handleUseContactInfoToggle(e.target.checked)}
                disabled={isContactInfoUsedByOthers}
                className="sr-only"
              />
              <div
                className={`flex size-5 items-center justify-center rounded ${
                  useContactInfo
                    ? 'bg-primary'
                    : isContactInfoUsedByOthers
                      ? 'border border-gray-300 bg-gray-100'
                      : 'border-secondary-text border'
                }`}
              >
                {useContactInfo && (
                  <svg
                    className="size-3 text-white"
                    viewBox="0 0 12 10"
                    fill="none"
                  >
                    <path
                      d="M1 5L4.5 8.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span
                className={`leading-6 tracking-[0.08px] ${isContactInfoUsedByOthers ? 'text-gray-400' : 'text-primary-text'}`}
              >
                {t('customerDetails.useContactInfo')}
              </span>
            </label>
          </div>

          <div className="space-y-4">
            <TextInput
              label={t('contact.name')}
              placeholder={t('payment.fullNamePlaceholder')}
              prefixIcon={<BsPerson className="size-4 text-gray-400" />}
              disabled={useContactInfo}
              error={errors.name?.message ? t(errors.name.message) : undefined}
              {...register('name')}
            />

            <PhoneNumberInput
              label={t('payment.phoneNumber')}
              placeholder={t('payment.phonePlaceholder')}
              telPrefix={watch('telPrefix') || '+66'}
              value={watch('tel') || ''}
              onChange={(val: PhoneNumberInputValue) => {
                setValue('telPrefix', val.dialCode, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
                setValue('tel', val.national, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              error={errors.tel?.message ? t(errors.tel.message) : undefined}
              disabled={useContactInfo}
            />

            <TextInput
              label={t('payment.email')}
              type="email"
              placeholder={t('payment.emailPlaceholder')}
              prefixIcon={<BsEnvelope className="size-4 text-gray-400" />}
              disabled={useContactInfo}
              error={
                errors.email?.message ? t(errors.email.message) : undefined
              }
              {...register('email')}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-secondary-text leading-6 font-semibold">
              {t('customerDetails.personalRemarks')}
            </h3>
            <TextInput
              placeholder={t('customerDetails.remarksPlaceholder')}
              prefixIcon={<TbMessage className="size-4 text-gray-400" />}
              {...register('remark')}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-secondary-text leading-6 font-semibold">
              {t('customerDetails.genderPreference')}
            </h3>

            <Dropdown
              placeholder={t('customerDetails.genderPreference')}
              options={genderOptions}
              value={genderPreference}
              onChange={(value) => setGenderPreference(value as Gender)}
              prefixIcon={<GoDash className="text-secondary-text size-6" />}
            />

            <Dropdown
              placeholder={t('customerDetails.specificTherapist')}
              options={therapistOptions}
              value={preferredTherapists}
              onChange={(value) => setPreferredTherapists(value as number[])}
              multiSelect={true}
              prefixIcon={<BsPerson className="text-secondary-text size-6" />}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-secondary-text leading-6 font-semibold">
              {t('customerDetails.avoidTherapist')}
            </h3>

            <Dropdown
              placeholder={t('customerDetails.avoidTherapist')}
              options={therapistOptions}
              value={unpreferredTherapists}
              onChange={(value) => setUnpreferredTherapists(value as number[])}
              multiSelect
              prefixIcon={<BsPerson className="text-secondary-text size-6" />}
            />
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-gray-200 bg-white p-4">
          <Button type="submit" className="w-full">
            {t('common.save')}
          </Button>
        </div>
      </form>
    </ResponsiveModalBottomSheet>
  );
};

export default CustomerDetailsFormModalBottomSheet;
